import React, { useEffect, useState } from 'react';
import { 
  Plus, Edit, Trash, Search, User, Eye, EyeOff, ChevronLeft, 
  ChevronRight, CheckCircle, Loader, Copy, Palette
} from 'lucide-react';
import { useAccounts } from '../hooks/useAccounts';
import { useServices } from '../hooks/useServices';
import AccountWhatsappButton from '../AccountWhatsappButton'; // NUEVO IMPORT

// Componente para el selector de color con botón de pincel
const ColorPickerButton = ({ editingProfile, setEditingProfile, themes, currentTheme }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowColorPicker(!showColorPicker)}
        className={`p-2 rounded-full ${themes[currentTheme].bgTertiary} hover:${themes[currentTheme].bgPrimary} border ${themes[currentTheme].borderColor} transition-colors flex items-center justify-center`}
        style={{ 
          backgroundColor: editingProfile.color || '#6b7280', 
          borderColor: editingProfile.color || '#6b7280'
        }}
        title="Cambiar color del perfil"
      >
        <Palette size={16} className="text-white drop-shadow-sm" />
      </button>
      
      {showColorPicker && (
        <>
          {/* Overlay para cerrar al hacer click fuera */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowColorPicker(false)}
          />
          
          {/* Panel del selector de color */}
          <div className={`absolute right-0 top-12 z-20 ${themes[currentTheme].bgSecondary} border ${themes[currentTheme].borderColor} rounded-lg p-4 shadow-lg min-w-[280px]`}>
            <h4 className={`text-sm font-medium ${themes[currentTheme].textPrimary} mb-3`}>Selector de Color</h4>
            
            <div className="space-y-3">
              <div className="flex gap-2 items-center">
                <input 
                  type="color" 
                  className={`w-12 h-10 rounded-md border ${themes[currentTheme].borderColor} cursor-pointer`}
                  value={editingProfile.color || '#3b82f6'}
                  onChange={(e) => setEditingProfile({ ...editingProfile, color: e.target.value })}
                />
                <input 
                  type="text" 
                  className={`flex-1 p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={editingProfile.color || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, color: e.target.value || null })}
                  placeholder="Automático (deja vacío)"
                />
              </div>
              
              {/* Colores predefinidos */}
              <div>
                <p className={`text-xs ${themes[currentTheme].textMuted} mb-2`}>Colores predefinidos:</p>
                <div className="grid grid-cols-6 gap-2">
                  {['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                        editingProfile.color === color ? 'border-white' : 'border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setEditingProfile({ ...editingProfile, color })}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              {/* Botón para resetear color */}
              <div className="pt-2 border-t border-gray-600">
                <button
                  type="button"
                  onClick={() => setEditingProfile({ ...editingProfile, color: null })}
                  className={`w-full p-2 text-xs rounded-md ${themes[currentTheme].bgTertiary} hover:${themes[currentTheme].bgPrimary} ${themes[currentTheme].textSecondary} transition-colors`}
                >
                  🎨 Usar colores automáticos
                </button>
                <p className={`text-xs ${themes[currentTheme].textMuted} mt-2`}>
                  Los colores automáticos cambian según el estado del perfil
                </p>
              </div>
              
              <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                Color para identificar visualmente este perfil
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AccountsSection = ({ themes, currentTheme, services, clients }) => {
  const {
    accounts,
    loading,
    error,
    setError,
    showAccountForm,
    setShowAccountForm,
    showAssignProfileForm,    
    setShowAssignProfileForm,
    showEditProfileForm,      
    setShowEditProfileForm,
    newAccount,
    setNewAccount,
    profileAssignment,
    paginationInfo,
    setProfileAssignment,
    editingProfile,
    setEditingProfile,
    currentAccountPage,
    itemsPerPage,
    searchTerm,
    setSearchTerm,
    clientSearchQuery,
    setClientSearchQuery,
    filteredClients,
    showClientResults,
    copySuccess,
    tooltipInfo,
    hiddenAccounts,
    showHiddenAccounts,
    setShowHiddenAccounts,
    serviceFilter,
    setServiceFilter,
    setCurrentAccountPage,
    expiryFilter,
    setExpiryFilter,
    fetchAccounts,
    handleSaveAccount,
    handleDeleteAccount,
    handleEditAccount,
    handleAssignProfile,
    handleEditProfile,
    handleSaveProfileAssignment,
    handleSaveEditedProfile,
    handleAddProfile,
    handleProfileChange,
    handleServiceChange,
    isProfileNearExpiry,
    isProfileExpired,
    filterClients,
    handleClientSearch,
    handleSelectClient,
    getPaginatedAccounts,
    getServiceName,
    showProfileTooltip,
    hideProfileTooltip,
    getClientName,
    toggleHideAccount,
    copyProfileInfo,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  } = useAccounts();

  const { getServiceTemplate } = useServices();

  // Cargar cuentas inicialmente
  useEffect(() => {
    fetchAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtros: reset página + recarga inmediata (sin delay)
  useEffect(() => {
    // Resetear página inmediatamente
    setCurrentAccountPage(1);
    
    // Aplicar throttling reducido para la recarga (reducido de 300ms a 10ms)
    const timer = setTimeout(() => {
      fetchAccounts(1, itemsPerPage);
    }, 10); // Delay reducido para mejor UX
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, serviceFilter, expiryFilter, showHiddenAccounts]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderTooltip = () => {
    if (!tooltipInfo.show) return null;
    
    return (
      <div 
        className="fixed z-50" 
        style={{ 
          left: `${tooltipInfo.position.x}px`, 
          top: `${tooltipInfo.position.y}px` 
        }}
      >
        {tooltipInfo.content}
      </div>
    );
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Cargando cuentas...</p>
        </div>
      </div>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-600 rounded-md text-white"
          onClick={() => {
            setError(null);
            fetchAccounts();
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${themes[currentTheme].textPrimary}`}>Listado de Cuentas</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${themes[currentTheme].textSecondary}`}>
              {showHiddenAccounts ? 'Solo cuentas ocultas' : 'Mostrar cuentas ocultas'}
            </span>
            <div 
              className={`relative w-10 h-5 rounded-full cursor-pointer ${showHiddenAccounts ? 'bg-purple-600' : 'bg-gray-600'}`}
              onClick={() => setShowHiddenAccounts(!showHiddenAccounts)}
            >
              <div 
                className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition-transform ${
                  showHiddenAccounts ? 'transform translate-x-5' : ''
                }`}
              ></div>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
              onClick={() => {
                setNewAccount({ 
                  serviceId: services.length > 0 ? services[0].id : '', 
                  username: '', 
                  password: '', 
                  observation: '',
                  profiles: services.length > 0 ? 
                    Array.from({ length: services[0].max_profiles || services[0].maxProfiles || 1 }, 
                      (_, index) => ({ name: `Perfil ${index + 1}`, status:'libre' })) 
                    : [{ name: '', status: 'libre' }]
                });
                setShowAccountForm(true);
              }}
            >
              <Plus size={20} /> Nueva
            </button>
          </div>
        </div>
      </div>
      
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-lg`}>
            <div className="flex flex-wrap gap-4">
              <div className="flex-grow">
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Buscar</label>
                <div className="relative">
                  <Search size={20} className={`absolute left-3 top-2.5 ${themes[currentTheme].textMuted}`} />
                  <input
                    type="text"
                    placeholder="Buscar por servicio, correo o observaciones..."
                    className={`w-full pl-10 p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Filtrar por servicio</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                >
                  <option value="">Todos los servicios</option>
                  {services && Array.isArray(services) && services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Estado</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                >
                  <option value="all">Todos los perfiles</option>
                  <option value="disponibles">Disponibles</option>
                  <option value="ocupados">Ocupados</option>
                  <option value="expired">Perfiles expirados</option>
                  <option value="nearExpiry">Próximos a expirar</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {accounts.length === 0 ? (
        <div className={`${themes[currentTheme].bgSecondary} p-8 rounded-lg text-center`}>
          <p className={themes[currentTheme].textMuted}>No hay cuentas registradas.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewAccount({ 
                serviceId: services.length > 0 ? services[0].id : '', 
                username: '', 
                password: '', 
                observation: '',
                profiles: services.length > 0 ? 
                  Array.from({ length: services[0].max_profiles || services[0].maxProfiles || 1 }, 
                    (_, index) => ({ name: `Perfil ${index + 1}`, status: 'libre' })) 
                  : [{ name: '', status: 'libre' }]
              });
              setShowAccountForm(true);
            }}
          >
            Crear la primera cuenta
          </button>
        </div>
      ) : (
        <div className={`${themes[currentTheme].bgSecondary} rounded-lg overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className={themes[currentTheme].bgPrimary}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>#</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Cuenta</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Contraseña</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Observación</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Perfiles</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={`${themes[currentTheme].bgSecondary} divide-y divide-gray-800`}>
              {getPaginatedAccounts(services).map((account, index) => {
                const displayIndex = (currentAccountPage - 1) * itemsPerPage + index + 1;
                
                return (
                  <tr key={account.id}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{displayIndex}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {(() => {
                          const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
                          return (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img 
                                  className="h-10 w-10 rounded-full" 
                                  src={service?.image || 'https://via.placeholder.com/48'} 
                                  alt="" 
                                />
                              </div>
                              <div className="ml-3 flex flex-col">
                                <span className={`text-sm font-medium ${themes[currentTheme].textPrimary}`}>
                                  {service?.name || 'N/A'}
                                </span>
                                <span className={`text-xs ${themes[currentTheme].textMuted}`}>
                                  {account.username}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{account.password}</td>
                    <td className={`px-6 py-4 text-sm ${themes[currentTheme].textSecondary}`}>
                      {account.observation ? (
                        <div className="relative group max-w-xs">
                          <div className="truncate cursor-pointer" title={account.observation}>
                            {account.observation}
                          </div>
                          {/* Tooltip que aparece al pasar el mouse */}
                          <div className="absolute left-0 top-0 mt-6 w-64 bg-gray-800 p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                            <p className="text-xs text-white whitespace-pre-wrap break-words">
                              {account.observation}
                            </p>
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themes[currentTheme].textSecondary}`}>
                      <div className="flex flex-wrap gap-2">
                        {account.profiles && account.profiles.map((profile) => {
                          let status = profile.status;
                          let profileColor = 'text-white';
                          let bgColor = 'bg-gray-700 bg-opacity-50';
                          
                          // Si el perfil tiene un color personalizado (no nulo y no el valor por defecto), usarlo
                          if (profile.color && profile.color !== '#3b82f6' && profile.status === 'ocupado') {
                            // Usar el color personalizado como fondo con opacidad
                            const customStyle = {
                              backgroundColor: profile.color + '40', // Añadir opacidad
                              borderColor: profile.color,
                              borderWidth: '2px'
                            };
                            
                            return (
                              <div 
                                key={profile.id} 
                                className="flex flex-col items-center p-2 rounded-md cursor-pointer border-2"
                                style={customStyle}
                                onClick={() => {
                                  if (profile.status === 'libre') {
                                    handleAssignProfile(account, profile, services);
                                  } else if (profile.status === 'ocupado') {
                                    handleEditProfile(account, profile);
                                  } else {
                                    alert(`Este perfil está ${profile.status}. No se puede asignar.`);
                                  }
                                }}
                                onMouseEnter={(e) => showProfileTooltip(e, profile, account, getServiceName(account.service_id || account.serviceId, services), clients)}
                                onMouseLeave={hideProfileTooltip}
                              >
                                <User size={24} className="text-white" style={{ color: profile.color }} />
                              </div>
                            );
                          }
                          
                          // Lógica original para perfiles sin color personalizado o perfiles libres
                          if (status === 'ocupado') {
                            if (isProfileExpired(profile)) {
                              status = 'expirado';
                              profileColor = 'text-red-400';
                              bgColor = 'bg-red-900 bg-opacity-30';
                            } else if (isProfileNearExpiry(profile)) {
                              profileColor = 'text-orange-400';
                              bgColor = 'bg-orange-900 bg-opacity-30';
                            } else {
                              profileColor = 'text-green-400';
                              bgColor = 'bg-green-900 bg-opacity-30';
                            }
                          } else if (status === 'expirado') {
                            profileColor = 'text-red-400';
                            bgColor = 'bg-red-900 bg-opacity-30';
                          }
                          
                          return (
                            <div 
                              key={profile.id} 
                              className={`flex flex-col items-center p-2 rounded-md cursor-pointer ${bgColor}`}
                              onClick={() => {
                                if (profile.status === 'libre') {
                                  handleAssignProfile(account, profile, services);
                                } else if (profile.status === 'ocupado') {
                                  handleEditProfile(account, profile);
                                } else {
                                  alert(`Este perfil está ${profile.status}. No se puede asignar.`);
                                }
                              }}
                              onMouseEnter={(e) => showProfileTooltip(e, profile, account, getServiceName(account.service_id || account.serviceId, services), clients)}
                              onMouseLeave={hideProfileTooltip}
                            >
                              <User size={24} className={profileColor} />
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary} flex gap-2`}>
                      {/* NUEVO BOTÓN DE WHATSAPP */}
                      <AccountWhatsappButton 
                        account={account}
                        services={services}
                        clients={clients}
                      />
                      
                      <button 
                        className="text-blue-400 hover:text-blue-500"
                        onClick={() => handleEditAccount(account)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-400 hover:text-red-500"
                        onClick={() => handleDeleteAccount(account.id)}
                      >
                        <Trash size={16} />
                      </button>
                      <button 
                        className={`${hiddenAccounts.includes(account.id) ? 'text-purple-400 hover:text-purple-500' : 'text-gray-400 hover:text-gray-300'}`}
                        onClick={() => toggleHideAccount(account.id)}
                        title={hiddenAccounts.includes(account.id) ? "Mostrar cuenta" : "Ocultar cuenta"}
                      >
                        {hiddenAccounts.includes(account.id) ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Paginación para cuentas */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${themes[currentTheme].textMuted}`}>Filas por página:</span>
          <select 
            className={`${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary} rounded-md px-2 py-1 text-sm`}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div className="flex items-center">
          <span className={`mr-4 text-sm ${themes[currentTheme].textMuted}`}>
            {paginationInfo.totalCount > 0 
              ? `${(paginationInfo.currentPage - 1) * paginationInfo.limit + 1}-${Math.min(paginationInfo.currentPage * paginationInfo.limit, paginationInfo.totalCount)} de ${paginationInfo.totalCount}` 
              : '0-0 de 0'}
          </span>
          <div className="flex space-x-1">
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${paginationInfo.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handlePrevPage}
              disabled={paginationInfo.currentPage === 1}
            >
              <ChevronLeft size={20} className={themes[currentTheme].textMuted} />
            </button>
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${paginationInfo.currentPage >= paginationInfo.totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleNextPage}
              disabled={paginationInfo.currentPage >= paginationInfo.totalPages}
            >
              <ChevronRight size={20} className={themes[currentTheme].textMuted} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal para formulario de cuenta */}
      {showAccountForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md max-h-screen overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>
              {newAccount.id ? 'Editar Cuenta' : 'Nueva Cuenta'}
            </h2>
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Servicio</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newAccount.serviceId}
                  onChange={(e) => handleServiceChange(e.target.value, services)}
                >
                  {!services || !Array.isArray(services) || services.length === 0 ? (
                    <option value="">No hay servicios disponibles</option>
                  ) : (
                    services.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cuenta</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newAccount.username}
                  onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                />
                <p className={`text-xs ${themes[currentTheme].textMuted} mt-1`}>Correo electrónico o nombre de usuario para acceder al servicio</p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Contraseña</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Observación</label>
                <textarea 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  rows="2"
                  placeholder="Información adicional sobre esta cuenta..."
                  value={newAccount.observation}
                  onChange={(e) => setNewAccount({ ...newAccount, observation: e.target.value })}
                ></textarea>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Perfiles</label>
                <div className="space-y-2">
                  {newAccount.profiles.map((profile, index) => (
                    <div key={index} className="grid grid-cols-3 gap-2">
                      <input 
                        type="text" 
                        className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                        placeholder="Nombre del perfil"
                        value={profile.name}
                        onChange={(e) => handleProfileChange(index, 'name', e.target.value)}
                      />
                      <select 
                        className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                        value={profile.status}
                        onChange={(e) => handleProfileChange(index, 'status', e.target.value)}
                      >
                        <option value="libre">Libre</option>
                        <option value="ocupado">Ocupado</option>
                        <option value="expirado">Expirado</option>
                      </select>
                      {profile.status === 'ocupado' ? (
                        <select 
                          className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                          value={profile.clientId || ''}
                          onChange={(e) => handleProfileChange(index, 'clientId', e.target.value || null)}
                        >
                          <option value="">Seleccionar cliente</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      ) : (
                        <div className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textMuted} flex items-center justify-center`}>
                          -
                        </div>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="text-sm text-blue-400 flex items-center gap-1"
                    onClick={handleAddProfile}
                  >
                    <Plus size={16} /> Agregar perfil
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => {
                    setShowAccountForm(false);
                    if (newAccount.id) {
                      setNewAccount({ serviceId: '', username: '', password: '', observation: '', profiles: [{ name: '', status: 'libre' }] });
                    }
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveAccount}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para asignar perfil a un cliente */}
      {showAssignProfileForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>Asignar Perfil a Cliente</h2>
            
            <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-md mb-4`}>
              <p className={`text-sm ${themes[currentTheme].textMuted}`}>Servicio: <span className={themes[currentTheme].textPrimary}>{profileAssignment.serviceName}</span></p>
              <p className={`text-sm ${themes[currentTheme].textMuted} mt-1`}>Perfil: <span className={themes[currentTheme].textPrimary}>{profileAssignment.profileName}</span></p>
              <p className={`text-sm ${themes[currentTheme].textMuted} mt-1`}>Precio de venta: <span className={`font-bold ${themes[currentTheme].textPrimary}`}>{formatCurrency(profileAssignment.customPrice || profileAssignment.servicePrice)}</span></p>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cliente</label>
                <div className="relative">
                  <input 
                    type="text" 
                    className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                    placeholder="Buscar cliente por nombre o teléfono..."
                    value={clientSearchQuery}
                    onChange={(e) => handleClientSearch(e, clients)}
                    onFocus={() => filterClients(clientSearchQuery, clients)}
                  />
                  
                  {showClientResults && filteredClients.length > 0 && (
                    <div className={`absolute z-10 mt-1 w-full ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} rounded-md shadow-lg max-h-60 overflow-auto`}>
                      {filteredClients.map(client => (
                        <div 
                          key={client.id}
                          className={`p-2 cursor-pointer hover:${themes[currentTheme].bgPrimary} ${
                            profileAssignment.clientId === client.id ? themes[currentTheme].bgPrimary : ''
                          }`}
                          onClick={() => handleSelectClient(client)}
                        >
                          <div className="flex justify-between">
                            <div>
                              <span className={themes[currentTheme].textPrimary}>{client.name}</span>
                              {client.type === 'revendedor' && (
                                <span className="ml-2 px-1 py-0.5 bg-purple-900 text-purple-300 text-xs rounded-full">
                                  Revendedor
                                </span>
                              )}
                            </div>
                            {client.phone && (
                              <span className={themes[currentTheme].textMuted}>{client.phone}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!profileAssignment.clientId && (
                  <p className="text-xs text-red-400 mt-1">
                    Selecciona un cliente para asignar el perfil
                  </p>
                )}
              </div>
              
              {profileAssignment.clientId && (
                <div className={`${themes[currentTheme].bgTertiary} p-3 rounded-md`}>
                  <p className={`text-sm font-medium ${themes[currentTheme].textSecondary}`}>Cliente seleccionado:</p>
                  <div className={`mt-1 p-2 ${themes[currentTheme].bgPrimary} rounded`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {getClientName(profileAssignment.clientId, clients)}
                          {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.type === 'revendedor' && (
                            <span className="ml-2 px-1 py-0.5 bg-purple-900 text-purple-300 text-xs rounded-full">
                              Revendedor
                            </span>
                          )}
                        </p>
                        {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.phone && (
                          <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                            {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.phone}
                          </p>
                        )}
                      </div>
                      <button 
                        onClick={() => {
                          setProfileAssignment({...profileAssignment, clientId: ''});
                          setClientSearchQuery('');
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Cambiar
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Precio de Venta</label>
                <input 
                  type="number" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={profileAssignment.customPrice || profileAssignment.servicePrice}
                  onChange={(e) => setProfileAssignment({ ...profileAssignment, customPrice: parseFloat(e.target.value) || 0 })}
                  placeholder="Precio personalizado"
                  min="0"
                  step="1000"
                />
                <p className={`text-xs ${themes[currentTheme].textMuted} mt-1`}>
                  Precio por defecto: {formatCurrency(profileAssignment.servicePrice)}
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>PIN</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={profileAssignment.pin}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Solo permitir números y máximo 6 caracteres
                    if (/^[0-9]*$/.test(value) && value.length <= 6) {
                      setProfileAssignment({ ...profileAssignment, pin: value });
                    }
                  }}
                  maxLength="6"
                  placeholder="Ingrese un PIN de 4 a 6 dígitos"
                />
                {profileAssignment.pin && profileAssignment.pin.length > 0 && profileAssignment.pin.length < 4 && (
                  <p className="text-xs text-red-400 mt-1">
                    El PIN debe tener entre 4 y 6 dígitos
                  </p>
                )}
                {profileAssignment.pin && !/^[0-9]*$/.test(profileAssignment.pin) && (
                  <p className="text-xs text-red-400 mt-1">
                    Solo se permiten números
                  </p>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Fecha de Vencimiento</label>
                <input 
                  type="date" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={profileAssignment.expiryDate}
                  onChange={(e) => setProfileAssignment({ ...profileAssignment, expiryDate: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => setShowAssignProfileForm(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-green-600 rounded-md text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSaveProfileAssignment(services, clients, null, getServiceTemplate)}
                  disabled={loading || !profileAssignment.clientId || (profileAssignment.pin && (profileAssignment.pin.length < 4 || profileAssignment.pin.length > 6 || !/^[0-9]+$/.test(profileAssignment.pin)))}
                >
                  {loading ? <><Loader size={16} className="animate-spin" /> Guardando...</> : 
                  copySuccess ? <><CheckCircle size={16} /> Copiado</> : <><Copy size={16} /> Guardar y Copiar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal para editar perfil existente */}
      {showEditProfileForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md relative`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>Editar Perfil</h2>
            
            {/* Botón de selector de color en la esquina superior derecha */}
            <div className="absolute top-4 right-4">
              <ColorPickerButton 
                editingProfile={editingProfile}
                setEditingProfile={setEditingProfile}
                themes={themes}
                currentTheme={currentTheme}
              />
            </div>
            
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Nombre</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={editingProfile.name}
                  onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Estado</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={editingProfile.status}
                  onChange={(e) => setEditingProfile({ ...editingProfile, status: e.target.value })}
                >
                  <option value="libre">Libre</option>
                  <option value="ocupado">Ocupado</option>
                  <option value="expirado">Expirado</option>
                </select>
              </div>


              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Observaciones</label>
                <textarea 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  rows="3"
                  placeholder="Notas adicionales sobre este perfil..."
                  value={editingProfile.observations}
                  onChange={(e) => setEditingProfile({ ...editingProfile, observations: e.target.value })}
                ></textarea>
              </div>
              
              {editingProfile.status === 'ocupado' && (
                <>
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cliente</label>
                    <select 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.clientId}
                      onChange={(e) => setEditingProfile({ ...editingProfile, clientId: e.target.value })}
                    >
                      <option value="">Seleccionar cliente</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={`${themes[currentTheme].bgTertiary} p-3 rounded-md`}>
                    <p className={`text-sm font-medium ${themes[currentTheme].textSecondary}`}>Cliente seleccionado:</p>
                    <div className={`mt-1 p-2 ${themes[currentTheme].bgPrimary} rounded`}>
                      <p className="font-medium">{getClientName(editingProfile.clientId, clients)}</p>
                      {clients.find(c => c.id === parseInt(editingProfile.clientId))?.phone && (
                        <p className={`text-xs ${themes[currentTheme].textMuted}`}>{clients.find(c => c.id === parseInt(editingProfile.clientId))?.phone}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>PIN</label>
                    <input 
                      type="text" 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.pin}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Solo permitir números y máximo 6 caracteres
                        if (/^[0-9]*$/.test(value) && value.length <= 6) {
                          setEditingProfile({ ...editingProfile, pin: value });
                        }
                      }}
                      maxLength="6"
                      placeholder="Ingrese un PIN de 4 a 6 dígitos"
                    />
                    {editingProfile.pin && editingProfile.pin.length > 0 && editingProfile.pin.length < 4 && (
                      <p className="text-xs text-red-400 mt-1">
                        El PIN debe tener entre 4 y 6 dígitos
                      </p>
                    )}
                    {editingProfile.pin && !/^[0-9]*$/.test(editingProfile.pin) && (
                      <p className="text-xs text-red-400 mt-1">
                        Solo se permiten números
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Fecha de Vencimiento</label>
                    <input 
                      type="date" 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.expiryDate}
                      onChange={(e) => setEditingProfile({ ...editingProfile, expiryDate: e.target.value })}
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-between pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-purple-600 rounded-md text-white flex items-center gap-2"
                  onClick={() => copyProfileInfo(services, getServiceTemplate)}
                  disabled={loading}
                >
                  {copySuccess ? 
                    <><CheckCircle size={16} /> Copiado</> : 
                    <><Copy size={16} /> Copiar Info</>}
                </button>
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                    onClick={() => setShowEditProfileForm(false)}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-blue-600 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSaveEditedProfile}
                    disabled={loading || (editingProfile.status === 'ocupado' && (!editingProfile.clientId || (editingProfile.pin && (editingProfile.pin.length < 4 || editingProfile.pin.length > 6 || !/^[0-9]+$/.test(editingProfile.pin)))))}
                  >
                    {loading ? <><Loader size={16} className="animate-spin mr-1" /> Guardando...</> : 'Guardar Cambios'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {renderTooltip()}
    </div>
  );
};

export default AccountsSection;
